import * as React from 'react';
import { useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert, Link,
  IconButton, Menu, MenuItem, Divider
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { api } from '../../lib/api';
import { saveAuth } from '../../lib/auth';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import { LANGS } from '../../i18n/langs';
import loginI18n from '../../i18n/pages/auth/login.i18n';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';

const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
};

export default function Login() {
  const { t, register, lang, setLang } = useI18n();
  React.useEffect(() => { register('login', loginI18n); }, [register]);

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];
  const [langAnchor, setLangAnchor] = React.useState(null);

  const nav = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: '', password: '' });
  const [fieldErr, setFieldErr] = useState({ email: '', password: '' });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFieldErr((fe) => ({ ...fe, [e.target.name]: '' }));
  };

  const validate = () => {
    const fe = {};
    const email = (form.email || '').trim().toLowerCase();
    if (!email) fe.email = t('login.errors.emailRequired');
    else if (!/.+@.+\..+/.test(email)) fe.email = t('login.errors.emailInvalid');
    if (!form.password) fe.password = t('login.errors.passwordRequired');
    setFieldErr(fe);
    return Object.keys(fe).length === 0;
  };

  const formatServerError = (error) => {
    const status = error?.response?.status;
    const msg = String(error?.response?.data?.message || '').toLowerCase();

    if (status === 400) {
      if (msg.includes('email and password')) return t('login.errors.requiredBoth');
      if (msg.includes('invalid credentials')) return t('login.errors.invalid');
      return t('login.errors.invalid');
    }
    if (status === 401 || status === 403) return t('login.errors.forbidden');
    if (status === 429) return t('login.errors.throttled');
    if (status >= 500) return t('login.errors.server');
    if (!status) return t('login.errors.offline');
    return t('login.errors.invalid');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        email: (form.email || '').trim().toLowerCase(),
        password: form.password,
      };
      const { data } = await api.post('/auth/login', payload);
      saveAuth(data);

      const role = data.user?.role;
      const redirectTo = location.state?.redirectTo;
      const pendingSummary = location.state?.pendingSummary;

      let stored = null;
      try { stored = JSON.parse(sessionStorage.getItem('redirectAfterAuth') || 'null'); } catch {}
      const target = redirectTo || stored?.redirectTo;
      const summary = pendingSummary || stored?.summary;

      if (target) {
        try { sessionStorage.removeItem('redirectAfterAuth'); } catch {}
        return nav(target, { replace: true, state: { summary } });
      }

      if (role === 'admin' || role === 'superadmin') {
        nav('/admin', { replace: true });
      } else {
        nav('/mes-envois', { replace: true });
      }
    } catch (error) {
      const message = formatServerError(error);
      setErr(message);
      const msg = String(error?.response?.data?.message || '').toLowerCase();
      if (msg.includes('invalid credentials')) {
        setFieldErr((fe) => ({ ...fe, password: t('login.errors.invalid') }));
      }
    } finally {
      setLoading(false);
    }
  };

  const goHome = () => nav('/services', { replace: true });

  return (
    <Box sx={{ bgcolor: '#f7faf8', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
          {/* barre bleue */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BLUE }} />

          {/* sélecteur de langue */}
          <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton
              onClick={(e) => setLangAnchor(e.currentTarget)}
              size="small"
              sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}
            >
              <Box component="img" src={currentLang.flag} alt={currentLang.alt} sx={{ height: 18, width: 'auto', mr: 0.5 }} />
              <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </IconButton>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {LANGS.map((l) => (
                <MenuItem
                  key={l.code}
                  onClick={() => { setLang(l.code); setLangAnchor(null); }}
                  selected={l.code === lang}
                  sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}
                >
                  <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Bouton retour accueil */}
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              onClick={goHome}
              startIcon={<HomeRoundedIcon />}
              variant="text"
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                color: BLUE,
                px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: BLUE_HOVER },
              }}
            >
              {t('login.home.cta')}
            </Button>
          </Box>

          <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, mb: 0.5 }}>
            {t('login.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
            {t('login.subtitle')}
          </Typography>

          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              name="email"
              label={t('login.form.email')}
              type="email"
              fullWidth
              required
              margin="normal"
              value={form.email}
              onChange={handleChange}
              error={Boolean(fieldErr.email)}
              helperText={fieldErr.email || ' '}
              sx={textFieldSx}
            />
            <TextField
              name="password"
              label={t('login.form.password')}
              type="password"
              fullWidth
              required
              margin="normal"
              value={form.password}
              onChange={handleChange}
              error={Boolean(fieldErr.password)}
              helperText={fieldErr.password || ' '}
              sx={textFieldSx}
            />

            {/* Lien mot de passe oublié */}
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover" sx={{ fontWeight: 700, color: BLUE }}>
                {t('login.form.forgot')}
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              sx={{ mt: 2, py: 1.25, fontWeight: 700, textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}
            >
              {loading ? t('login.form.loading') : t('login.form.submit')}
            </Button>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: 14.5 }}>
              {t('login.signup.prompt')}{' '}
              <Link component={RouterLink} to="/register" underline="hover" sx={{ fontWeight: 700, color: BLUE }}>
                {t('login.signup.link')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
